import { FlexProps, GridItemProps, GridProps } from '@chakra-ui/react';
import {
  IConfirmModalProps,
  TaxProviderTypeEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { FC } from 'react';
import { IconType } from 'react-icons/lib';
import {
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MImage,
  MText,
} from '../../../../../components/Monetize';
import {
  ActionType,
  DataTableActions,
} from '../../../../../components/Monetize/DataTable/MDataTableActions';
import { MEntityStatusBadge } from '../../../../../components/Monetize/MEntityStatusBadge';
import {
  TAX_PROVIDER_DISPLAY,
  TAX_STATUS_DISPLAY,
} from '../../../../../constants/taxes';

export type TaxCardAction = 'STATUS_UPDATE' | 'VERIFY' | 'DELETE' | 'OPEN';

interface TaxCardViewProps extends FlexProps {
  type: TaxProviderTypeEnum;
  status: TaxStatusEnum;
  omitEditButton?: boolean;
  tags: Array<{ label: string; value: string } & GridItemProps>;
  actions: {
    action: TaxCardAction;
    label: string;
    isDanger?: boolean;
    confirmProps?: Partial<IConfirmModalProps> | null;
    icon?: IconType;
  }[];
  onEdit: () => void;
  onAction: (action: TaxCardAction) => void;
  tagsContainer?: GridProps;
}

export const TaxCardView: FC<TaxCardViewProps> = ({
  type,
  tags,
  actions,
  status,
  omitEditButton,
  onEdit,
  onAction,
  tagsContainer,
  ...rest
}) => {
  const tableActions: ActionType[] = actions.map(
    ({ action, isDanger, label, confirmProps, icon }) => ({
      title: label,
      action: () => onAction(action),
      color: isDanger ? 'danger' : undefined,
      enabled: true,
      confirmProps,
      icon,
    }),
  );

  return (
    <MFlex
      bgColor="tWhite.base"
      alignItems="flex-start"
      flexDirection="column"
      p={4}
      border="1px solid"
      borderColor="tGray.back"
      borderRadius="3px"
      w="600px"
      {...rest}
    >
      <MFlex justifyContent="space-between" w="100%" minH={8}>
        <MFlex alignItems="center">
          <MImage
            src={TAX_PROVIDER_DISPLAY[type].icon}
            maxW="80px"
            maxH="25px"
            objectFit="contain"
          />
          <MText ml={2} fontSize="14px">
            {TAX_PROVIDER_DISPLAY[type].name}
          </MText>
          <MEntityStatusBadge status={TAX_STATUS_DISPLAY[status]} />
        </MFlex>
        <MFlex alignItems="center">
          {!omitEditButton && (
            <MButton variant="tertiary" size="sm" onClick={onEdit}>
              Edit
            </MButton>
          )}
          <DataTableActions actions={tableActions} />
        </MFlex>
      </MFlex>
      <MDivider my={4} />
      <MGrid w="full" {...tagsContainer}>
        {tags.map(({ label, value, ...gridItemProps }, index) => (
          <MGridItem
            key={label}
            mb={index !== tags.length - 1 ? 4 : 0}
            {...gridItemProps}
          >
            <MFormField label={label} overflow="hidden">
              <MText title={value}>{value}</MText>
            </MFormField>
          </MGridItem>
        ))}
      </MGrid>
    </MFlex>
  );
};
